import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Send } from "lucide-react";
import { api, dynamicLabel } from "../lib/api";
import { logError } from "../lib/logger";
import { useAuth } from "../context/AuthContext";

export default function ChatPage() {
  const { matchId } = useParams();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [other, setOther] = useState(null);
  const [text, setText] = useState("");
  const bottomRef = useRef(null);

  const loadMessages = useCallback(async () => {
    const { data } = await api.get(`/matches/${matchId}/messages`);
    setMessages(data);
  }, [matchId, setMessages]);

  useEffect(() => {
    api
      .get("/matches")
      .then((res) => {
        const m = res.data.find((x) => x.match_id === matchId);
        if (m) setOther(m.user);
      })
      .catch((error) => logError("Failed to load match partner:", error));
  }, [matchId, setOther]);

  useEffect(() => {
    loadMessages();
    const interval = setInterval(loadMessages, 3000);
    return () => clearInterval(interval);
  }, [loadMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    setText("");
    const { data } = await api.post(`/matches/${matchId}/messages`, { text: t });
    setMessages((m) => [...m, data]);
  };

  return (
    <div className="max-w-3xl mx-auto px-6 sm:px-12 h-[calc(100vh-4rem)] flex flex-col" data-testid="chat-page">
      <div className="py-6 border-b border-[#E5E0D8] flex items-center gap-4">
        <Link to="/matches" data-testid="chat-back-link" className="text-[#5C5C5C] hover:text-[#1A1A1A] transition-colors duration-300">
          <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
        </Link>
        {other && (
          <div className="flex items-center gap-4">
            {other.photo_url && (
              <img src={other.photo_url} alt={other.name} className="w-10 h-10 object-cover" />
            )}
            <div>
              <h1 className="font-serif text-xl" data-testid="chat-partner-name">{other.name}</h1>
              <p className="text-xs uppercase tracking-[0.15em] text-[#7A3B3F]">
                {dynamicLabel(other.relationship_dynamic)}
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto py-8 space-y-4" data-testid="chat-messages">
        {messages.length === 0 && (
          <p className="text-center text-[#8A8A8A] py-12">
            You both said yes. Begin the conversation with intention.
          </p>
        )}
        {messages.map((m) => {
          const mine = m.sender_id === user?.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div
                data-testid={`message-${m.id}`}
                className={`max-w-[75%] px-5 py-3 leading-relaxed ${
                  mine
                    ? "bg-[#7A3B3F] text-[#FAF9F6]"
                    : "bg-[#F3F0EA] border border-[#E5E0D8] text-[#1A1A1A]"
                }`}
              >
                {m.text}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={send} className="py-6 border-t border-[#E5E0D8] flex gap-4">
        <input
          data-testid="chat-message-input"
          className="flex-1 bg-transparent border-b border-[#E5E0D8] rounded-none px-0 py-3 focus:outline-none focus:border-[#7A3B3F] transition-colors duration-300 placeholder:text-[#8A8A8A]"
          placeholder="Write something true…"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <button
          data-testid="chat-send-button"
          type="submit"
          className="bg-[#7A3B3F] text-[#FAF9F6] px-6 py-3 hover:bg-[#5D2B2E] transition-colors duration-300"
        >
          <Send className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </form>
    </div>
  );
}
